import { Component } from '@theme/component';

/**
 * @typedef {object} CardSwipeRefs
 * @property {HTMLAnchorElement} imageLink
 * @property {HTMLImageElement} primaryImage
 * @property {HTMLImageElement} [bufferImage]
 * @property {HTMLButtonElement} [nextButton]
 * @property {HTMLScriptElement} [imageData]
 * @property {HTMLElement} [progressFill]
 * @property {HTMLElement} [status]
 */

/** @extends {Component<CardSwipeRefs>} */
class TeylensCardSwipe extends Component {
  requiredRefs = ['imageLink', 'primaryImage'];
  #mode = window.matchMedia('(max-width: 749px), (hover: none), (pointer: coarse)');
  #images = [];
  #index = 0;
  #wantedIndex = 0;
  #slot = 0;
  #generation = 0;
  #listeners;
  #pointer = null;
  #suppressClickUntil = 0;
  #source;
  #preloads = new Map();

  connectedCallback() {
    super.connectedCallback();
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.#mode.addEventListener('change', this.#reset, { signal });
    this.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.addEventListener('mousedown', this.#onMouseDown, { signal });
    this.addEventListener('pointermove', this.#onPointerMove, { signal });
    this.addEventListener('pointerup', this.#onPointerUp, { signal });
    this.addEventListener('pointercancel', this.#cancelPointer, { signal });
    this.addEventListener('click', this.#onClick, { capture: true, signal });
    this.addEventListener('keydown', this.#onKeyDown, { signal });
    this.#readImages();
    this.#reset();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#readImages();
    this.#reset();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#generation++;
    this.#pointer = null;
  }

  #readImages() {
    const { imageData, primaryImage } = this.refs;
    const rawData = imageData?.textContent || '[]';
    const sourceKey = `${rawData}\n${this.dataset.initialImage}\n${this.dataset.initialSource}`;
    if (this.#source === sourceKey) return;
    this.#source = sourceKey;
    this.#preloads.clear();
    try {
      const data = JSON.parse(rawData);
      this.#images = data.map((image) => typeof image === 'string' ? { src: image } : image).filter((image) => image.src || image.url);
    } catch {
      this.#images = [];
    }
    const initialId = this.dataset.initialImage;
    let initial = this.#images.findIndex((image) => String(image.id) === initialId);
    if (initial < 0 && this.dataset.initialSource) {
      const initialPath = new URL(this.dataset.initialSource, window.location.href).pathname;
      initial = this.#images.findIndex((image) => new URL(image.src || image.url, window.location.href).pathname === initialPath);
    }
    if (initial > 0) this.#images.unshift(...this.#images.splice(initial, 1));
    if (this.#images.length && primaryImage) {
      this.#images[0].initialSrc = primaryImage.src;
      this.#images[0].initialSrcset = primaryImage.srcset;
    }
    this.dataset.imageCount = String(Math.max(this.#images.length, 1));
    this.style.setProperty('--ty-swipe-count', String(Math.max(this.#images.length, 1)));
  }

  #reset = () => {
    this.#generation++;
    this.#pointer = null;
    this.#suppressClickUntil = 0;
    this.#index = 0;
    this.#wantedIndex = 0;
    this.#slot = 0;
    const { primaryImage, bufferImage, nextButton, progressFill, status } = this.refs;
    const first = this.#images[0];
    if (first?.initialSrc) {
      primaryImage.srcset = first.initialSrcset;
      primaryImage.src = first.initialSrc;
      primaryImage.alt = first.alt || this.refs.imageLink.getAttribute('aria-label') || '';
    }
    primaryImage.setAttribute('data-active', 'true');
    primaryImage.removeAttribute('aria-hidden');
    bufferImage?.removeAttribute('data-active');
    bufferImage?.setAttribute('aria-hidden', 'true');
    if (nextButton) nextButton.hidden = !this.#mode.matches || this.#images.length < 2;
    if (progressFill) progressFill.style.transform = 'translateX(0%)';
    if (status) status.textContent = '';
    this.dataset.currentIndex = '0';
  };

  nextImage() {
    this.#step(1);
  }

  #step(direction) {
    if (!this.#mode.matches || this.#images.length < 2) return;
    this.#select((this.#wantedIndex + direction + this.#images.length) % this.#images.length);
  }

  async #select(index) {
    this.#wantedIndex = index;
    const generation = ++this.#generation;
    if (index === this.#index) return;
    const image = this.#images[index];
    const current = this.#slot === 0 ? this.refs.primaryImage : this.refs.bufferImage;
    const next = this.#slot === 0 ? this.refs.bufferImage : this.refs.primaryImage;
    if (!image || !current || !next) return;
    const source = new URL(image.src || image.url, window.location.href);
    if (source.protocol !== 'https:' && source.protocol !== 'http:') return;
    const atWidth = (width) => {
      const url = new URL(source);
      url.searchParams.set('width', String(width));
      return url.href;
    };
    next.loading = 'eager';
    next.sizes = this.refs.primaryImage.sizes || '50vw';
    next.srcset = [240, 400, 600, 900].map((width) => `${atWidth(width)} ${width}w`).join(', ');
    next.src = atWidth(900);
    next.alt = image.alt || this.refs.imageLink.getAttribute('aria-label') || '';
    if (image.width) next.width = image.width;
    if (image.height) next.height = image.height;
    try {
      await next.decode();
    } catch {
      if (generation === this.#generation) this.#wantedIndex = this.#index;
      return;
    }
    if (generation !== this.#generation || !this.isConnected || !this.#mode.matches || !next.naturalWidth) return;
    current.removeAttribute('data-active');
    current.setAttribute('aria-hidden', 'true');
    next.setAttribute('data-active', 'true');
    next.removeAttribute('aria-hidden');
    this.#slot = this.#slot === 0 ? 1 : 0;
    this.#index = index;
    this.dataset.currentIndex = String(index);
    if (this.refs.progressFill) this.refs.progressFill.style.transform = `translateX(${index * 100}%)`;
    if (this.refs.status) this.refs.status.textContent = `${index + 1} / ${this.#images.length}`;
  }

  #onPointerDown = (event) => {
    if (!this.#mode.matches || this.#images.length < 2 || !event.isPrimary || event.button !== 0) return;
    event.stopPropagation();
    this.#suppressClickUntil = 0;
    this.#pointer = { id: event.pointerId, x: event.clientX, y: event.clientY, axis: null };
    this.#warmAdjacent();
  };

  #onMouseDown = (event) => {
    if (this.#mode.matches && this.#images.length > 1) event.stopPropagation();
  };

  #warmAdjacent() {
    for (const direction of [-1, 1]) {
      const index = (this.#wantedIndex + direction + this.#images.length) % this.#images.length;
      const image = this.#images[index];
      if (!image || this.#preloads.has(index)) continue;
      const source = new URL(image.src || image.url, window.location.href);
      if (!['https:', 'http:'].includes(source.protocol)) continue;
      const preload = new Image();
      preload.sizes = this.refs.primaryImage.sizes || '50vw';
      preload.srcset = [240, 400, 600, 900].map((width) => {
        const url = new URL(source);
        url.searchParams.set('width', String(width));
        return `${url.href} ${width}w`;
      }).join(', ');
      source.searchParams.set('width', '900');
      preload.src = source.href;
      this.#preloads.set(index, preload);
    }
    for (const index of this.#preloads.keys()) {
      const distance = (index - this.#wantedIndex + this.#images.length) % this.#images.length;
      if (distance > 1 && distance < this.#images.length - 1) this.#preloads.delete(index);
    }
  }

  #onPointerMove = (event) => {
    const pointer = this.#pointer;
    if (!pointer || pointer.id !== event.pointerId) return;
    const dx = event.clientX - pointer.x;
    const dy = Math.abs(event.clientY - pointer.y);
    if (!pointer.axis && Math.max(Math.abs(dx), dy) >= 8) {
      if (Math.abs(dx) > dy * 1.1) pointer.axis = 'x';
      else if (dy > Math.abs(dx) * 1.1) {
        this.#pointer = null;
        return;
      }
    }
    if (pointer.axis !== 'x') return;
    event.stopPropagation();
    if (!this.hasPointerCapture(event.pointerId)) this.setPointerCapture(event.pointerId);
    this.refs.imageLink.style.transform = `translateX(${Math.max(-36, Math.min(36, dx * 0.35))}px)`;
  };

  #onPointerUp = (event) => {
    const pointer = this.#pointer;
    if (!pointer || pointer.id !== event.pointerId) return;
    this.#cancelPointer();
    const dx = event.clientX - pointer.x;
    const dy = Math.abs(event.clientY - pointer.y);
    if (Math.abs(dx) < 14 || (pointer.axis !== 'x' && Math.abs(dx) <= dy * 1.1)) return;
    event.stopPropagation();
    this.#suppressClickUntil = performance.now() + 500;
    this.#step(dx < 0 ? 1 : -1);
  };

  #cancelPointer = () => {
    const pointer = this.#pointer;
    this.#pointer = null;
    this.refs.imageLink.style.transform = '';
    if (pointer && this.hasPointerCapture(pointer.id)) this.releasePointerCapture(pointer.id);
  };

  #onClick = (event) => {
    if (performance.now() >= this.#suppressClickUntil) return;
    this.#suppressClickUntil = 0;
    event.preventDefault();
    event.stopImmediatePropagation();
  };

  #onKeyDown = (event) => {
    if (!this.#mode.matches || this.#images.length < 2) return;
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    event.stopPropagation();
    this.#step(event.key === 'ArrowLeft' ? -1 : 1);
  };
}

if (!customElements.get('teylens-card-swipe-component')) {
  customElements.define('teylens-card-swipe-component', TeylensCardSwipe);
}
